import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { css } from '@emotion/core';
import i18n from 'i18next';
import Checkbox from '@material-ui/core/Checkbox';

import { fieldWrapperStyle } from 'common/style/fields.style';
import { generateSectionStyle } from 'common/style/cssStyleType';
import withSettings from 'editor/components/FormContent/Field/withSettings';
import ExpandableText from 'client/components/FormContent/ExpandableText';
import { setTextVariantStyle } from 'common/utils/variantStyle';

function FieldTerm({
    fieldId,
    description,
    value,
    enableExternalURL,
    externalURL,
    error,
    touched,
    updateFormValue,
    settingStyle,
    setFieldTouched
}) {
    const [isExpanded, setIsExpanded] = useState(true);
    const [shouldExpandedError, setShouldExpandedError] = useState(false);
    const { displayNameStyle } = generateSectionStyle(settingStyle);
    const lineHeight = setTextVariantStyle('subtitle2', 'line-height');
    const fontSize = setTextVariantStyle('subtitle2', 'font-size');

    const isFieldError = shouldExpandedError || (error && touched);
    const fixExternalUrl = url => {
        if (url) {
            const regex = new RegExp(/^(http|https):\/\//);
            return url.match(regex) ? url : `http://${url}`;
        }
        return '';
    };

    const handleExpandChange = useCallback(expanded => {
        setIsExpanded(expanded);
        if (expanded) {
            setShouldExpandedError(false);
            return;
        }
    }, []);

    const renderExpandableText = () => (
        <ExpandableText
            text={description}
            lineClamp={3}
            style={descStyle}
            hiddenExpandBtn
            onExpandChange={handleExpandChange}
        />
    );

    return (
        <div css={css(fieldWrapperStyle, displayNameStyle)} id={`section_${fieldId}`}>
            <div css={css(termWrapperStyle)}>
                <Checkbox
                    checked={Boolean(value[fieldId])}
                    onChange={e => {
                        const {
                            target: { checked }
                        } = e;
                        if (!isExpanded && checked) {
                            setShouldExpandedError(true);
                            return;
                        }
                        updateFormValue({ [fieldId]: checked });
                        setFieldTouched(fieldId);
                    }}
                    size="medium"
                    color="primary"
                    css={checkboxStyle(isFieldError)}
                />
                {enableExternalURL ? (
                    <a
                        href={fixExternalUrl(externalURL)}
                        css={linkStyle}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {renderExpandableText()}
                    </a>
                ) : (
                    renderExpandableText()
                )}
            </div>
            {isFieldError && (
                <span css={css(errorStyle({ fontSize, lineHeight }))} id="feedback">
                    {shouldExpandedError ? i18n.t('form.field_must_expand_term') : error}
                </span>
            )}
        </div>
    );
}

FieldTerm.propTypes = {
    fieldId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    enableExternalURL: PropTypes.bool,
    externalURL: PropTypes.string,
    description: PropTypes.string,
    value: PropTypes.shape({}),
    error: PropTypes.string,
    touched: PropTypes.bool,
    updateFormValue: PropTypes.func,
    settingStyle: PropTypes.object,
    setFieldTouched: PropTypes.func
};

export default withSettings(FieldTerm);

const errorStyle = ({ fontSize, lineHeight }) => `
    color: #D52027;    
    font-size: ${fontSize}px;
    line-height: ${lineHeight};
    margin-left: 32px;
`;

const linkStyle = css`
    color: inherit;
`;

const termWrapperStyle = css`
    display: flex;
    align-items: flex-start;
`;

const descStyle = `
    padding: 2px 0px 2px 8px;
`;

const checkboxStyle = isError => css`
    padding: 0;
    color: ${isError ? '#D52027;' : 'inherit'};
    &.MuiCheckbox-colorPrimary.Mui-checked {
        color: inherit;
    }
`;
